// Shared inner body for the Oscar account wish-list create/update form (the
// main WishListForm plus the shared-emails inline formset). Renders inside
// AccountPageBody (the account side-nav shell) without the document chrome --
// each site wraps this in its own PageSkeleton so the DOM is byte-identical to
// the pre-move AccountPageShell. This form does NOT fit the unified
// AccountFormBody path: it carries subform machinery, a product prompt, and a
// history-back cancel, none of which that shared body models.
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { FormErrorList } from "../../../forms/FormErrorList";
import { FieldControl } from "../../../forms/FormPageFields";
import {
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_HAS_ERRORS_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
} from "../../../forms/classNames";
import { getErrorListElemID } from "../../../forms/hooks/formUtils";
import { AccountPageBody } from "../AccountPageBody";
import { FormFields } from "../FormFields";

type ISubform = Immutable<
    templates.WishListFormDocumentTemplate["shared_emails_subforms"][number]
>;

// Renders one shared-email subform. Each row carries its own label + errors
// (resolved server-side), so no prefixed-html_name keying is needed here.
const SharedEmailSubform = (
    props: Immutable<{ subform: ISubform }>,
): React.JSX.Element => (
    <>
        {props.subform.rows.map((row) => {
            const field = row.field;
            if (field.kind === "hidden") {
                return <FieldControl key={field.field_id} field={field} />;
            }
            const hasErrors = row.errors.length > 0;
            const errorListId = hasErrors
                ? getErrorListElemID(field.field_id)
                : undefined;
            return (
                <div
                    className={concatClassNames([
                        FORM_FIELD_CLASS_NAME,
                        field.required ? FORM_FIELD_REQUIRED_CLASS_NAME : null,
                        hasErrors ? FORM_FIELD_HAS_ERRORS_CLASS_NAME : null,
                    ])}
                    key={field.field_id}
                >
                    <label
                        className={FORM_LABEL_CLASS_NAME}
                        htmlFor={field.field_id}
                    >
                        {row.label}
                    </label>
                    <FieldControl
                        field={field}
                        ariaDescribedBy={errorListId}
                        ariaInvalid={hasErrors}
                    />
                    {hasErrors && (
                        <FormErrorList id={errorListId} errors={row.errors} />
                    )}
                </div>
            );
        })}
    </>
);

type IProps = Immutable<
    templates.WishListFormDocumentTemplate & {
        /** Opt-in required-field legend (Tempur). Other brands leave default-off. */
        showRequiredLegend?: boolean;
    }
>;

export const WishListFormBody = (props: IProps): React.JSX.Element => {
    const {
        action_url,
        is_update,
        new_list_prompt,
        product_prompt_before,
        product_prompt_title,
        product_prompt_after,
        shared_emails_management_fields,
        shared_emails_subforms,
        share_heading,
        share_help,
        save_label,
        saving_label,
        or_label,
        cancel_label,
        showRequiredLegend,
    } = props;
    return (
        <AccountPageBody sideNav={props.side_nav}>
            <form method="post" action={action_url} data-behaviours="lock">
                <CSRFToken />
                {shared_emails_management_fields.map((field) => (
                    <FieldControl key={field.field_id} field={field} />
                ))}

                {!is_update && <p>{new_list_prompt}</p>}

                <FormFields
                    fields={props.fields}
                    field_labels={props.field_labels}
                    field_errors={props.field_errors}
                    non_field_errors={props.non_field_errors}
                    showRequiredLegend={showRequiredLegend}
                />

                {product_prompt_title && (
                    <p>
                        {product_prompt_before}
                        <strong>{product_prompt_title}</strong>
                        {product_prompt_after}
                    </p>
                )}

                <div className="wishlist-shared-emails mt-5">
                    <h3>{share_heading}</h3>
                    <small>{share_help}</small>
                    {shared_emails_subforms.map((subform, i) => (
                        <SharedEmailSubform key={i} subform={subform} />
                    ))}
                </div>
                <div className="form-group">
                    <button
                        className="btn btn-lg btn-primary"
                        type="submit"
                        data-loading-text={saving_label}
                    >
                        {save_label}
                    </button>{" "}
                    {or_label}{" "}
                    <button
                        type="button"
                        onClick={() => getWindow()?.history.go(-1)}
                    >
                        {cancel_label}
                    </button>
                    .
                </div>
            </form>
        </AccountPageBody>
    );
};
